import type { TimePeriod } from "./types";

/**
 * @deprecated This function is no longer used. Chart data now includes both:
 * - `periodStart` (Date object) for logic operations (filtering, sorting)
 * - `dateLabel` (string) for display
 * No parsing is needed anymore.
 */
export const parseDataDate = (dateString: string, timePeriod: TimePeriod) => {
  if (timePeriod === "monthly") {
    // Format: "2024 Dec" - year and month
    return new Date(dateString);
  } else {
    // Format: "Dec 16" - month and day, need to add current year
    return new Date(`${dateString}, ${new Date().getFullYear()}`);
  }
};

/**
 * Calculate ISO week number for a given date (relative to start of year).
 * Week 1 is the first week of January containing a Thursday.
 * @param date - The date to calculate the week number for
 * @returns The ISO week number (1-53)
 *
 * @example
 * getWeekNumber(new Date('2025-01-13'))  // 3
 * getWeekNumber(new Date('2025-01-01'))  // 1
 */
export const getWeekNumber = (date: Date): number => {
  // Use UTC throughout: period dates are UTC-midnight ISO strings, so local-time
  // getters would shift the week number by one for viewers west of UTC.
  const startOfYear = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const pastDaysOfYear = (date.getTime() - startOfYear.getTime()) / 86400000;
  return Math.ceil((pastDaysOfYear + startOfYear.getUTCDay() + 1) / 7);
};

/**
 * Formats a date based on the time period granularity.
 * @param date - The date to format
 * @param timePeriod - The time period granularity ('daily', 'weekly', or 'monthly')
 * @param weekNumber - Optional week number for weekly formatting
 * @returns Formatted date string
 *
 * @example
 * formatDateByPeriod(new Date('2025-01-15'), 'daily')        // "Jan 15"
 * formatDateByPeriod(new Date('2025-01-13'), 'weekly', 3)    // "Week 3 - w/c Jan 13"
 * formatDateByPeriod(new Date('2025-01-15'), 'monthly')      // "2025 Jan"
 */
export const formatDateByPeriod = (date: Date, timePeriod: TimePeriod, weekNumber?: number): string => {
  // Period dates arrive as UTC-midnight ISO strings (e.g. "2025-11-01"). Format in UTC
  // so the label always matches the bucket the data was aggregated into — without
  // `timeZone: "UTC"` a viewer west of UTC sees the previous day/month (e.g. Nov → Oct).
  switch (timePeriod) {
    case "daily":
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
    case "weekly": {
      const mondayDate = date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
      // If no week number provided, calculate it from the date
      const week = weekNumber ?? getWeekNumber(date);
      return `Week ${week} - w/c ${mondayDate}`;
    }
    case "monthly":
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", timeZone: "UTC" });
    default:
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  }
};
